import gql from 'graphql-tag';
import { compose } from 'ramda';

import { selectParams } from '#src/store/location/selectors';
import { setInvite } from '#src/store/content/actions';
import {
  setIsQaTeam,
  setSuperchargerInviteToken,
} from '#src/store/session/actions';
import { toNotFound } from '#src/store/location/actions';
import { authenticateWithTokenIfPresent } from '#src/lib/routes/guards';
import { superchargerGameFieldsFragment } from '#src/lib/game-utils/fragments/supercharger-game-fields-fragment';
import { selectTimeZone } from '#src/store/intl/selectors';
import { isAuthenticated } from '#src/store/session/selectors';

const query = gql`
  query Invite($token: String, $timeZone: String!, $isAuthenticated: Boolean!) {
    invite(token: $token) {
      id
      token
      type
      creditAmountCents
      gameId
      game {
        id
        ...SuperchargerGameFieldsFragment
      }
      userId
      user {
        id
        firstName
        displayName
        avatarV2 {
          value
          type
        }
      }
    }
  }

  ${superchargerGameFieldsFragment}
`;

// A paying member's free spot for a friend (FS2): who gave it and whether
// it can still be taken. The invite's game and user (the member who gave
// it) come from the query above; the host is the game's smsHost.
const freeSpotQuery = gql`
  query SuperchargerFreeSpotInvite($token: String!) {
    superchargerFreeSpotInvite(token: $token) {
      inviterFirstName
      status
      expiresAt
    }
  }
`;

const FREE_SPOT_INVITE_TYPE = 'SuperchargerFreeSpotInvite';

export const path = '/invite/:inviteToken';
export const component = 'InviteLanding';
export const thunk =
  ({ ctx, graphqlClient }) =>
  async (dispatch, getState) => {
    // Opportunistically upgrades the session when the app's "open in hub"
    // deep link carries a `?token=` (see authenticateWithTokenIfPresent) -
    // same as /games/:gameId. It only redirects to drop a used token from
    // the URL on the server (the page reloads without it), so this is
    // harmless for the real invite-token flow, which has no `?token=`
    // query param to find.
    // ctx is passed through so a fresh SSR hit (the deep link's very first
    // request) can forward the auth service's Set-Cookie onto the real
    // response - see authTokenLogin's own ctx handling.
    if (await authenticateWithTokenIfPresent(dispatch, getState, ctx)) return;

    const { inviteToken, is_qa_team: isQaTeam } = selectParams(getState());
    if (isQaTeam === 'true') dispatch(setIsQaTeam(true));

    const timeZone = selectTimeZone(getState());
    const { data } = await graphqlClient.query({
      query,
      fetchPolicy: 'network-only',
      variables: {
        token: inviteToken,
        timeZone,
        isAuthenticated: isAuthenticated(getState()),
      },
    });

    const { invite } = data || {};
    if (!invite) {
      dispatch(toNotFound());
      return;
    }
    if (invite.type === FREE_SPOT_INVITE_TYPE) {
      const { data: freeSpotData } = await graphqlClient.query({
        query: freeSpotQuery,
        fetchPolicy: 'network-only',
        variables: { token: invite.token },
      });
      // Null when notify doesn't know the spot or its game's free spots
      // are off.
      const freeSpot = freeSpotData?.superchargerFreeSpotInvite;
      if (!freeSpot) {
        dispatch(toNotFound());
        return;
      }
      compose(dispatch, setInvite)({ ...invite, freeSpot });
      return;
    }

    compose(dispatch, setInvite)(invite);
    // See setSuperchargerInviteToken's own comment - only the GLP Won
    // signup funnel ever reads this back, so setting it unconditionally
    // here (even for a non-Supercharger invite) is harmless.
    dispatch(setSuperchargerInviteToken(invite.token));
  };
